import * as React from 'react';
import { observer } from 'mobx-react';
import styled from 'styled-components';
import { Row } from 'elc-grid';
import { translate, ITranslationsCollection, ServiceView, config } from 'elc-service';
import { ContentSpacing, breakpoint, Breakpoints } from 'elc-base-theme';
import { DEFAULT_NUMBER_OF_STARS } from '../../constants/ProductReview';
import {
    ProductSizePicker,
    ISizePicker,
    ISizePickerConfigs
} from '../product-size-picker/ProductSizePicker';
import { classes } from '../../utils/Classes';
import { Sku } from '../../domain/entities/Sku';
import { IAsset } from '../../domain/entities/Asset';
import { ProductItemWrapper } from '../../views/common/ProductItemWrapper';
import { ProductName } from './ProductName';
import { ProductDescription } from './ProductDescription';
import { ISizePickerImages, IShortDescriptionConfig } from '../../interfaces/IProdcat';
import { TOGGLE_TYPE } from '../../constants/AutoReplenish';
import { PRODUCT_RATING, PRODUCT_RATING_WRAPPER } from 'elc-service-view-names';
import { ITranslationsOverwrites } from '../../../exported/product-grid-wrapper/ProductGridWrapper';
import { ProductPriceFormatted } from '../common/ProductPriceFormatted';
import { ProductPriceRangeRow } from '../common/ProductPriceRangeRow';
import { PriceFormatted } from 'elc-formatters';
import ProductInstallmentsPrice, {
    IProductInstallmentsPrice
} from '../../../internal/views/product-installments-price/ProductInstallmentsPrice';
import { ComponentTypes } from '../../constants/ComponentTypes';
import ProductShadePicker from '../../../exported/product-shade-picker/ProductShadePicker';
import { IShadePickerConfig, IShadeSwatches } from '../../interfaces/IShadePicker';
import ProductCTA from '../../../exported/product-cta/ProductCTA';
import { diContainer, serviceNames } from '../../../service-setup/diContainer';
import { Desktop, Mobile } from 'elc-layouts';
import { ProductQuickViewButton } from '../product-quickview/ProductQuickViewButton';
import { InventoryStatusMessage } from '../common/InventoryStatusMessage';
import { ADD_TO_BAG } from '../../constants/CTATypes';
import { IProductRatingConfig } from '../../../exported/product-rating/ProductRatingWrapper';
import { Product } from '../../domain/entities/Product';
import { ProductQuickView } from '../product-quickview/ProductQuickView';

export interface IProductBriefDetail {
    selectedSku: Sku;
    showSubDisplayName?: boolean;
    hideProductPrice?: boolean;
    hideProductPriceRange?: boolean;
    hideProductRating?: boolean;
    hideReviewsNumber?: boolean;
    productGridHideReadReviewsLink?: boolean;
    ctaType?: string;
    enableNotifyMe?: boolean;
    enableQuickView?: boolean;
    enableMobileQuickView?: boolean;
    enableShadePicker?: boolean;
    enableNumberShades?: boolean;
    enableSizePicker?: boolean;
    shortDescriptionLocation?: IShortDescriptionConfig;
    enableProductDetailLink?: boolean;
    useInitialRepositoryInventory?: boolean;
    isUpdatedInventorySource?: boolean;
    enableQuickViewShortcut?: boolean;
    translations?: ITranslationsCollection;
    largeImages: IAsset[];
    enableLearnMoreLink?: boolean;
    autoReplenishOptions: number[];
    autoReplenishToggleType?: TOGGLE_TYPE;
    addToBagDataTestId?: string;
    sizePickerImages?: ISizePickerImages;
    hideProductPricePerUnit?: boolean;
    showProductQuantitySelector?: boolean;
    hideEmptyStars?: boolean;
    enableAutoReplenishment?: boolean;
    config?: {
        sizePicker?: ISizePickerConfigs;
        ratingsEnabled?: boolean;
        shadeSwatches?: IShadeSwatches;
    };
    translationsOverwrites?: ITranslationsOverwrites;
    productUrl: string;
    positionIndex?: number;
    product: Product;
    isProductQuickViewOpen: boolean;
    onShopNowClick(productUrl: string): void;
    onProductDescriptionClick(): void;
    onQuickViewButtonClick(skyId: string, positionIndex?: number): void;
    handleShadeSelect(name: string): void;
    handleSizeSelect(name: string): void;
    onProductQuickViewClose(): void;
}

const ProductBriefDetailWrapper = styled.div`
    margin-top: ${ContentSpacing.space10};
    padding: ${ContentSpacing.space6};
    padding-bottom: ${ContentSpacing.space16};
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    height: 100%;
    box-sizing: border-box;
`;

const RatingContainer = styled(Row)`
    justify-content: space-between;
`;

const ProductShadesNumber = styled(Row)`
    display: flex;
`;

const SizePickerContainer = styled(ProductSizePicker)`
    margin: 0;
`;

const StyledProductItemWrapper = styled(ProductItemWrapper)`
    position: 1;
    flex: 1;
`;

const ProductPricePerUnitRowWrapper = styled.div`
    margin: 0 ${ContentSpacing.space8} 0;
    ${breakpoint('desktop')`
        margin: 0;
    `};
`;

const ProductQuickViewShortcut = styled.div`
    display: contents;
`;

@config(['sizePicker', 'ratingsEnabled', 'shadeSwatches'])
@translate(['shadesLabel', 'quickViewButtonLabel', 'taxIncluded'])
@observer
export class ProductBriefDetail extends React.Component<IProductBriefDetail> {
    private productBriefDetailWrapperClassNames: string = classes('product-brief-detail-wrapper');

    public static displayName: string;
    private getProductName() {
        const { product, productUrl } = this.props;
        const { displayName, subDisplayName, subHeader } = product;

        return (
            <StyledProductItemWrapper component="product-name">
                <ProductName
                    productUrl={productUrl}
                    displayName={displayName}
                    subDisplayName={subDisplayName}
                    showSubDisplayName={this.props.showSubDisplayName}
                    subHeader={subHeader}
                    onProductNameClick={this.props.onProductDescriptionClick}
                />
            </StyledProductItemWrapper>
        );
    }

    private getShortDescription() {
        const {
            productUrl,
            product: { shortDescription }
        } = this.props;

        return (
            <StyledProductItemWrapper component="description">
                <ProductDescription
                    productUrl={productUrl}
                    description={shortDescription}
                    onProductDescriptionClick={this.props.onProductDescriptionClick}
                />
            </StyledProductItemWrapper>
        );
    }

    private getProductPrice() {
        const { priceRange } = this.props.product;
        const { formattedPrice, formattedOriginalPrice } = this.props.selectedSku;
        const { hideProductPricePerUnit } = this.props;
        const { formattedPricePerUnit } = this.props.selectedSku;
        const { taxIncluded } = this.props.translations as ITranslationsCollection;

        const productPricePerUnitRowWrapperClassNames = classes(
            'product-price-per-unit-row-wrapper'
        );
        const productPricePerUnitClassNames = classes('product-price-per-unit');
        const taxIncludedLabelWrapper = classes('product-tax-included-label-wrapper');

        return (
            <StyledProductItemWrapper component="price">
                <>
                    {taxIncluded && <div className={taxIncludedLabelWrapper}>{taxIncluded}</div>}
                    {(this.props.hideProductPriceRange || !priceRange) && (
                        <ProductPriceFormatted
                            formattedPrice={formattedPrice}
                            formattedOriginalPrice={formattedOriginalPrice}
                        />
                    )}
                    {!this.props.hideProductPriceRange && priceRange && (
                        <ProductPriceRangeRow priceRange={priceRange} />
                    )}
                    {!hideProductPricePerUnit && (
                        <ProductPricePerUnitRowWrapper
                            className={productPricePerUnitRowWrapperClassNames}
                        >
                            <PriceFormatted
                                className={productPricePerUnitClassNames}
                                formattedPrice={formattedPricePerUnit}
                            />
                        </ProductPricePerUnitRowWrapper>
                    )}
                </>
            </StyledProductItemWrapper>
        );
    }

    private getProductRating() {
        const productBriefConfig = this.props.config;
        const ratingsEnabled = productBriefConfig && productBriefConfig.ratingsEnabled;
        const { productUrl, hideProductRating, hideReviewsNumber, hideEmptyStars } = this.props;

        if (hideProductRating) {
            return null;
        }

        const ratingPropsConfig = {
            hideStars: hideEmptyStars,
            hideReviewsLabel: true,
            isDisabled: true,
            productLink: productUrl,
            numberOfStars: DEFAULT_NUMBER_OF_STARS,
            productId: this.props.product.productId,
            hideReviewsNumber,
            hideReadReviewsLink: false
        };

        return (
            <StyledProductItemWrapper component="rating">
                <RatingContainer>
                    {ratingsEnabled ? (
                        this.getProductRatingService(ratingPropsConfig)
                    ) : (
                        <ServiceView name={PRODUCT_RATING_WRAPPER} config={ratingPropsConfig} />
                    )}
                </RatingContainer>
            </StyledProductItemWrapper>
        );
    }

    getProductRatingService = (ratingPropsConfig: IProductRatingConfig) => (
        <ServiceView name={PRODUCT_RATING} config={ratingPropsConfig} />
    );

    private getShadePicker() {
        const {
            product: { shades, productId },
            selectedSku
        } = this.props;
        const { config: briefConfig } = this.props;
        const shadeSwatches = briefConfig && briefConfig.shadeSwatches;
        const shadeSwatchesProductBrief =
            shadeSwatches && shadeSwatches.overrides && shadeSwatches.overrides.productBrief;
        const shadeSwatchesConfig = { ...shadeSwatches, ...shadeSwatchesProductBrief };

        const productShadePickerProps: IShadePickerConfig = {
            shades,
            selectedShade: selectedSku.selectedShade,
            onShadeSelect: this.props.handleShadeSelect,
            separator: ' ',
            isCarousel: true,
            productId,
            shadeSwatchesConfig
        };

        return (
            <StyledProductItemWrapper component="shade-picker">
                <ProductShadePicker
                    config={productShadePickerProps}
                    viewController={diContainer.get(serviceNames.productShadePickerViewController)}
                />
            </StyledProductItemWrapper>
        );
    }

    private getNumberOfShadesOutput() {
        const { shades } = this.props.product;
        const { shadesLabel } = this.props.translations as ITranslationsCollection;
        const numShades = shades.length;
        const shadeOutput = `${numShades} ${shadesLabel}`;

        if (numShades <= 1) {
            return null;
        }

        return (
            <StyledProductItemWrapper component="shades-number">
                <ProductShadesNumber className={'elc-product-shades-number'}>
                    {shadeOutput}
                </ProductShadesNumber>
            </StyledProductItemWrapper>
        );
    }

    private getSizePicker() {
        // TO ADD - IS_SIZED, IS_SHADED, REVIEWS WHEN AVAILABLE IN THE DATA
        const { handleSizeSelect, hideProductPricePerUnit, product, selectedSku } = this.props;
        const productBriefConfig = this.props.config;
        let options = {};
        if (productBriefConfig && productBriefConfig.sizePicker) {
            options = productBriefConfig.sizePicker.product_grid;
        }
        if (hideProductPricePerUnit) {
            options = { ...options, hideProductPricePerUnit };
        }

        const productSizePickerProps: ISizePicker = {
            sizes: product.sizes,
            selectedSize: selectedSku.selectedSize,
            onSelect: handleSizeSelect,
            productId: product.productId,
            options
        };

        return (
            <StyledProductItemWrapper component="size">
                <SizePickerContainer {...productSizePickerProps} />
            </StyledProductItemWrapper>
        );
    }

    private getCTA() {
        // ProductCTA has its own translations and doesn't inherit from here
        const { translations, selectedSku, ...ctaProps } = this.props;

        const briefDetailsConfig = {
            selectedSku,
            skuId: selectedSku.skuId,
            ...ctaProps
        };

        return (
            <StyledProductItemWrapper component="cta">
                <ProductCTA
                    config={briefDetailsConfig}
                    viewController={diContainer.get(serviceNames.productCTAViewController)}
                />
            </StyledProductItemWrapper>
        );
    }

    private getProductInstallmentsPrice() {
        const { selectedSku } = this.props;

        const productInstallmentPriceProps: IProductInstallmentsPrice = {
            defaultPrice: Number(selectedSku.originalPrice),
            componentType: ComponentTypes.PRODUCT_BRIEF
        };

        return (
            <StyledProductItemWrapper component="installments">
                <ProductInstallmentsPrice {...productInstallmentPriceProps} />
            </StyledProductItemWrapper>
        );
    }

    public onQuickViewButtonClick = () => {
        this.props.onQuickViewButtonClick(this.props.selectedSku.skuId, this.props.positionIndex);
    };

    private renderQuickViewButton = () => {
        const { enableQuickViewShortcut } = this.props;
        const productQuickViewShortcutClassNames = classes(
            'product-quick-view-shortcut',
            'elc-product-item-wrapper',
            'elc-product-cta-wrapper'
        );
        const { quickViewButtonLabel } = this.props.translations as ITranslationsCollection;

        return (
            <ProductQuickViewShortcut className={productQuickViewShortcutClassNames}>
                <ProductQuickViewButton
                    label={quickViewButtonLabel}
                    onQuickViewButtonClick={this.onQuickViewButtonClick}
                    enableQuickViewShortcut={enableQuickViewShortcut}
                />
            </ProductQuickViewShortcut>
        );
    };

    private getQuickViewCTA() {
        const { enableQuickView, enableQuickViewShortcut } = this.props;

        if (!enableQuickView && !enableQuickViewShortcut) {
            return null;
        }

        return (
            <>
                <Desktop minWidth={Breakpoints.desktop}>
                    {() => (enableQuickView ? this.renderQuickViewButton() : null)}
                </Desktop>
                <Mobile maxWidth={Breakpoints.desktop - 1}>
                    {() =>
                        enableQuickView && this.props.enableMobileQuickView
                            ? this.renderQuickViewButton()
                            : null
                    }
                </Mobile>
            </>
        );
    }

    private getQuickView() {
        const quickViewConfig = {
            hideProductRating: this.props.hideProductRating,
            productGridHideReadReviewsLink: this.props.productGridHideReadReviewsLink,
            enableLearnMoreLink: this.props.enableLearnMoreLink,
            autoReplenishOptions: this.props.autoReplenishOptions,
            autoReplenishToggleType: this.props.autoReplenishToggleType,
            sizePickerImages: this.props.sizePickerImages,
            hideProductPricePerUnit: this.props.hideProductPricePerUnit,
            showProductQuantitySelector: this.props.showProductQuantitySelector,
            enableAutoReplenishment: this.props.enableAutoReplenishment,
            enableNotifyMe: this.props.enableNotifyMe,
            shortDescriptionLocation: this.props.shortDescriptionLocation || {}
        };

        const quickViewProps = {
            product: this.props.product,
            onShadeSelect: this.props.handleShadeSelect,
            onSizeSelect: this.props.handleSizeSelect,
            onProductQuickViewClose: this.props.onProductQuickViewClose,
            selectedSku: this.props.selectedSku
        };

        return <ProductQuickView config={quickViewConfig} {...quickViewProps} />;
    }

    public render() {
        const {
            shortDescriptionLocation,
            hideProductPrice,
            enableShadePicker,
            enableNumberShades,
            enableSizePicker,
            product,
            hideProductPricePerUnit,
            ctaType,
            isProductQuickViewOpen
        } = this.props;

        // for a case where we have a product that is not sized and the price is hidden
        // but the pricePerUnit is true because of legal agreement, we will enforce it
        const enforceShowPrice = enableSizePicker && !product.isSized && !hideProductPricePerUnit;
        const displayInvMessage = ctaType && ctaType === ADD_TO_BAG;

        return (
            <ProductBriefDetailWrapper className={this.productBriefDetailWrapperClassNames}>
                {this.getProductName()}
                {shortDescriptionLocation &&
                    shortDescriptionLocation.product_brief &&
                    product.shortDescription &&
                    this.getShortDescription()}
                {(!hideProductPrice || enforceShowPrice) && this.getProductPrice()}
                {enableNumberShades && product.isShaded && this.getNumberOfShadesOutput()}
                {this.getProductRating()}
                {enableShadePicker && product.isShaded && this.getShadePicker()}
                {enableSizePicker && product.isSized && this.getSizePicker()}
                {this.getQuickViewCTA()}
                {isProductQuickViewOpen && this.getQuickView()}
                {this.getCTA()}
                {this.getProductInstallmentsPrice()}
                {displayInvMessage && (
                    <InventoryStatusMessage
                        config={this.props.config}
                        inventoryStatus={this.props.selectedSku.inventoryStatus}
                    />
                )}
            </ProductBriefDetailWrapper>
        );
    }
}

ProductBriefDetail.displayName = 'ProductBriefDetail';
